import {toast} from '@wandb/weave/common/components/elements/Toast';
import {Button} from '@wandb/weave/components';
import React, {useCallback, useEffect, useRef, useState} from 'react';

import * as RunFeed from '../../util/runfeed';
import {EMPTY_CONFIG} from '../../util/runfeed';
import * as TableCols from '../../util/tablecols';
import {InstrumentedLoader as Loader} from '../utility/InstrumentedLoader';
import {WBTableColumn} from '../WBTable/types';
import {useAutoColsRuns} from './hooks/useAutoColsRuns';
import {RunSelectorRunsPartialVars} from './types';

interface RunsAutoColsBaseProps {
  isInReport?: boolean;
  onClick(): void;
}

const RunsAutoColsBaseButton = (props: RunsAutoColsBaseProps) => {
  const {onClick, isInReport, ...passThrough} = props;

  return (
    <Button
      aria-label="Automatically show the most useful columns."
      icon="magic-wand-star"
      variant="secondary"
      onClick={() => {
        onClick();
        const actionLocation = isInReport ? 'report' : 'runs table';
        window.analytics?.track('Autohide columns button clicked', {
          location: actionLocation,
        });
      }}
      tooltip={'Automatically show the most useful columns.'}
      tooltipProps={{
        className: 'py-8 px-12',
      }}
      {...passThrough}></Button>
  );
};

const RunsAutoColsLoadingButton = (props: RunsAutoColsProps) => {
  const {
    query,
    tableSettings,
    setTableSettings,
    currentPage,
    columns,
    ...passThrough
  } = props;

  const {isLoading, runs} = useAutoColsRuns(query);
  const once = useRef(false);

  useEffect(() => {
    if (!isLoading && tableSettings != null && !once.current) {
      once.current = true;
      const pageSize = tableSettings.pageSize ?? EMPTY_CONFIG.pageSize;
      const i = pageSize * (currentPage - 1);
      const rows = runs.slice(i, i + pageSize);
      setTableSettings(
        TableCols.deriveAutoColumns(tableSettings, rows, columns)
      );
    }
  }, [isLoading, runs, setTableSettings, tableSettings, currentPage, columns]);

  return (
    <Button variant="secondary" {...passThrough}>
      <Loader
        size="tiny"
        active
        inline
        name="run-auto-cols-loading-button-loader"
      />
    </Button>
  );
};

interface RunsAutoColsProps {
  query: RunSelectorRunsPartialVars;
  tableSettings?: RunFeed.Config;
  isInReport?: boolean;
  currentPage: number;
  columns: WBTableColumn[];

  setTableSettings(config: RunFeed.Config): void;
}

export const RunsAutoCols = (props: RunsAutoColsProps) => {
  const {
    query,
    tableSettings,
    isInReport,
    setTableSettings,
    currentPage,
    columns,
  } = props;
  const [loading, setLoading] = useState(false);

  const setTableSettingsWrapped = useCallback(
    (config: RunFeed.Config) => {
      setTableSettings(config);
      setLoading(false);
      toast('Now showing recommended columns.');
    },
    [setTableSettings, setLoading]
  );

  const startLoad = useCallback(() => setLoading(true), [setLoading]);

  return loading ? (
    <RunsAutoColsLoadingButton
      query={query}
      tableSettings={tableSettings}
      setTableSettings={setTableSettingsWrapped}
      currentPage={currentPage}
      columns={columns}
    />
  ) : (
    <RunsAutoColsBaseButton onClick={startLoad} isInReport={isInReport} />
  );
};
